import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/safeClient';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushSubscription() {
  const { user } = useAuth();
  const subscribedRef = useRef(false);
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  // Fetch VAPID public key from server
  useEffect(() => {
    const fetchKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-vapid-key');
        if (!error && data?.publicKey) {
          setVapidKey(data.publicKey);
        }
      } catch (e) {
        console.error('[Push] Failed to fetch VAPID key:', e);
      }
    };
    fetchKey();
  }, []);

  const saveSubscription = async (subscription: PushSubscription, userId: string) => {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    const { error } = await supabase
      .from('push_subscriptions')
      .upsert(
        {
          user_id: userId,
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,endpoint' }
      );

    if (error) {
      console.error('[Push] Failed to save subscription:', error);
    }
  };

  const subscribe = useCallback(async () => {
    if (!user || !vapidKey || subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const reg = registration as ServiceWorkerRegistration & { pushManager: PushManager };

      // Check if already subscribed
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        await saveSubscription(existing, user.id);
        subscribedRef.current = true;
        return;
      }

      // Android Chrome 84+ treats requestPermission called without a user gesture
      // as an "abusive" request and may silently block it. To avoid this, we only
      // auto-request when permission is still 'default' (not yet asked), and we
      // accept that Android may show a quieter prompt. If already denied, stop here.
      if (Notification.permission === 'denied') return;

      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();

      if (permission !== 'granted') return;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      await saveSubscription(subscription, user.id);
      subscribedRef.current = true;
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
    }
  }, [user, vapidKey]);

  // Auto-subscribe on mount / when user or vapidKey becomes available
  useEffect(() => {
    subscribe();
  }, [subscribe]);

  // Watch for permission state changes (e.g. user enables notifications in Android
  // settings after initially dismissing the prompt). When permission flips to
  // 'granted', re-attempt subscription automatically.
  useEffect(() => {
    if (!('permissions' in navigator)) return;

    let permissionStatus: PermissionStatus | null = null;

    navigator.permissions.query({ name: 'notifications' as PermissionName }).then((status) => {
      permissionStatus = status;
      const onChange = () => {
        if (status.state === 'granted') {
          subscribedRef.current = false; // allow re-subscription
          subscribe();
        }
      };
      status.addEventListener('change', onChange);
    }).catch(() => {
      // Some browsers don't support querying notification permission
    });

    return () => {
      permissionStatus?.removeEventListener('change', () => {});
    };
  }, [subscribe]);

  return { subscribe };
}
